<template>
  <div
    id="tab-feedback"
    class="tab-feedback"
  >
    <div class="tab-feedback-content">
      <div class="feedback-rating">
        <div
          class="tab-feedback-top"
          :class="{
            light: isLightTheme,
            dark: !isLightTheme,
          }"
        >
          <div class="form-title">
            {{ $t("How's the presentation?") }}
          </div>
          <div class="rating">
            <div class="rating-content">
              <div class="rating-slider">
                <input
                  v-model="marked"
                  class="rating-slider-input"
                  @change="onChangeRate"
                  type="range"
                  name="rating-slider"
                  min="0"
                  max="3"
                  step="1"
                >
              </div>

              <div
                class="rating-bar"
                :style="{
                  '--width': `${marked * 33.33}%`
                }"
              />
              <div class="rating-section">
                <div
                  v-for="index in [0,1,2,3]"
                  :key="index"
                  class="rating-checkpoint"
                  :style="{
                    left: `${index * 33.33}%`
                  }"
                >
                  <span class="rating-checkpoint-placeholder" />
                </div>
                <div
                  class="rating-marker"
                  :class="{
                    'max': marked == 3,
                    'min': marked == 0
                  }"
                  :style="{
                    left: `${marked * 33.33}%`
                  }"
                >
                  <span class="rating-marker-placeholder">
                    <img :src="rating[marked].marker" alt="">
                  </span>
                  <span class="rating-marker-title">{{ rating[marked].title }}</span>
                </div>
              </div>
            </div>
          </div> <!-- /.rating -->
        </div>
      </div>

      <div class="tab-container">
        <div class="text-box" :class="{error: feedbackMsg.length > 200}">
          <textarea
            v-model="feedbackMsg"
            @focus="onFocusCommentInput"
            rows="3"
            :placeholder="$t('Leave feedback. It’s private...')"
            :maxlength="maxFeedbackMsgLength"
          />
          <div class="count-char">
            {{ maxFeedbackMsgLength - feedbackMsg.length }}
          </div>
        </div>
        <div class="horizontal layout center">
          <div class="avatar">
            <div v-if="!checkAudienceName()" class="default-ava">
              <i class="fas fa-user" />
            </div>
            <div
              v-if="checkAudienceName()"
              class="color-ava"
              :style="{ 'background-color': audienceColor}"
            >
              {{ $store.state.questionerName | textAvatar }}
            </div>
          </div>
          <input
            v-model="$store.state.questionerName"
            type="text"
            :placeholder="_getPlaceHolder()"
            class="flex input-name"
            :class="{error: (20 - $store.state.questionerName.length) < 0}"
            maxlength="20"
            @input="saveAudienceName()"
          >
          <button
            class="button-submit horizontal layout center center-justified"
            :disabled="checkDisableSubmit()"
            @click="sendFeedback()"
          >
            {{ $t('Send') }}
            <span v-if="submitting" class="loading-spinner" />
          </button>
        </div>
        <div v-if="textError" class="text-error text-danger">
          {{ textError }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import Config from '../config'
import Utils from '../util'
import storage from '../services/storage'

const markerA = require('@/assets/images/feedback/could_be_better.svg')
const markerB = require('@/assets/images/feedback/not_bad.svg')
const markerC = require('@/assets/images/feedback/great.svg')
const markerD = require('@/assets/images/feedback/cant_wait.svg')

export default {
  data() {
    return {
      submitting: false,
      feedbackMsg: '',
      maxFeedbackMsgLength: 200,
      audienceColor: '',
      textError: '',
      marked: 2,
      rating: [
        {
          marker: markerA,
          title: this.$t('Could be better'),
        },
        {
          marker: markerB,
          title: this.$t('Not bad'),
        },
        {
          marker: markerC,
          title: this.$t('Good'),
        },
        {
          marker: markerD,
          title: this.$t("Can't wait for the next"),
        },
      ],
    }
  },
  computed: {
    ...mapState({
      audienceId: (state) => state.audienceId,
      presentation: (state) => state.presentation,
      textColour: (state) => state.slide.textColour,
    }),
    isLightTheme() {
      return this.$store.state.slide.textColour === '#313131'
    },
  },
  mounted() {
    if (!this.audienceId) {
      this.audienceColor = '#313131'
    } else {
      this.audienceColor = Utils.getColorByStr(this.audienceId)
    }
    this.preloadMarkerIcons()

    const presentationId = this.$store.state.presentation.id
    this.$store.state.questionerName = storage.getValueByPresentation(
      presentationId,
      'questionerName',
      '',
    )

    if (this.$store.state.audienceName && !this.$store.state.questionerName) {
      this.$store.state.questionerName = this.$store.state.audienceName
      this.saveAudienceName()
    }
  },
  methods: {
    hideFeedbackForm() {
      this.$store.state.showFeedbackForm = false
    },
    preloadMarkerIcons() {
      this.rating.forEach((mark) => {
        const img = new Image()
        img.src = mark.marker
        img.onload = () => {}
      })
    },
    checkAudienceName() {
      const name = this.$store.state.questionerName
      if (!name.trim()) {
        return false
      }
      return true
    },

    saveAudienceName() {
      this.textError = ''
      const {
        questionerName,
        presentation: { id: presentationId },
      } = this.$store.state
      storage.setValueByPresentation(
        presentationId,
        'questionerName',
        questionerName,
      )
    },

    checkDisableSubmit() {
      return this.feedbackMsg.length > 200 || this.submitting
    },

    onChangeRate(e) {
      Utils.gaTrackEvent('Audience', 'Audience feedback > Rate presentation', e.target.value)
    },

    onFocusCommentInput() {
      Utils.gaTrackEvent('Audience', 'Audience feedback > Focus input')
    },

    async sendFeedback() {
      storage.updateValueByPresentation(
        this.presentation.id,
        'audienceId',
        this.audienceId,
      )
      this.textError = ''
      const title = this.feedbackMsg.trim()
      let { questionerName } = this.$store.state
      if (questionerName) {
        questionerName = questionerName.trim()
      }

      if (
        questionerName &&
        questionerName.length > 20
      ) {
        this.textError = this.$('Your name is limited to {length} characters.', {
          length: 20,
        })
        return
      }
      if (this.submitting) return
      this.submitting = true
      const payload = {
        audience: this.audienceId,
        audienceColor: this.audienceColor,
        presentationId: this.$store.state.presentation.id,
        voteCount: parseInt(this.marked, 10),
        title,
      }
      if (questionerName) {
        payload.audienceName = questionerName
      }
      try {
        await this.$http.post(Config.api.sendFeedback, payload)
        Utils.showToastSuccess(this.$t('Feedback sent!'))
        Utils.gaTrackEvent('Audience', 'Audience feedback > Submit', this.feedbackMsg)
        this.feedbackMsg = ''
      } catch (error) {
        this.textError = this.$t('Something went wrong, please try again.')
      } finally {
        this.submitting = false
      }
    },

    _getPlaceHolder() {
      return this.$t('enterYourNameOptional')
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.tab-feedback {
  input[type='text'] {
    height: 40px;
    border: none;
    background-color: #fff;
    padding-right: 8px;
    min-width: 50px;
    border-radius: 4px;
    border: 1px solid #ddd;
  }

  .button-submit {
    width: 90px;
    font-size: 14px;
    text-transform: capitalize;
    padding: 0;
    text-align: center;
    height: 40px;
    line-height: 40px;
    font-weight: bold;
    border-radius: 20px;
    background-color: $brand-a100;

    i {
      font-size: 14px;
    }
    .loading-spinner {
      margin-left: 4.5px;
      width: 1.2em;
      height: 1.2em;
    }
  }
}

.feedback-rating {
  margin: 0 auto;
  padding-top: 16px;
  padding-bottom: 16px;
  padding-left: 16px;
  padding-right: 16px;
  max-width: 600px;
}

.tab-feedback-top {
  padding: 20px;
  border-radius: 4px;
  &.light {
    background: rgba(85, 96, 132, 0.05);
  }

  &.dark {
    background: rgba(255, 255, 255, 0.2);

  }

}

.form-title {
  font-weight: 700;
  text-align: center;
}

.input-name {
  padding-left: 8px;
  margin-right: 8px;
  line-height: 40px;
  -webkit-appearance: none;

  &.error {
    color: $danger-color !important;
  }
}

.text-error {
  text-align: center;
  font-size: 14px;
}

.tab-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 16px 16px 16px;
}

.text-box {
  position: relative;

  textarea {
    width: 100%;
    padding: 16px;
    border-radius: 4px;
    border: 1px solid #ddd;
    -webkit-appearance: none;
    outline: none;
    padding-bottom: 20px;
  }
  .count-char {
    position: absolute;
    bottom: 8px;
    right: 8px;
    font-size: 14px;
    color: #666;
  }

  &.error {
    textarea {
      border-color: $danger-color;
      color: $danger-color;
    }

    .count-char {
      color: $danger-color;
    }
  }
}

.avatar {
  .default-ava,
  .color-ava {
    background-color: $primary-color;
    width: 32px;
    height: 32px;
    text-align: center;
    line-height: 32px;
    border-radius: 50%;
    margin: 16px;
    border: 1px solid #fff;
    margin-right: 8px;
    margin-left: 0;

    i {
      color: #fff;
    }
  }

  .color-ava {
    color: #fff;
    text-transform: uppercase;
    font-size: 13px;
    font-weight: bold;
  }
}

.rating {
  position: relative;

  &-content {
    position: relative;
    max-width: 250px;
    margin-left: auto;
    margin-right: auto;
    padding-top: 26px;
    padding-bottom: 30px;
  }

  &-slider {
    position: relative;

    &-input {
      opacity: 0;
      position: absolute;
      z-index: 1;
      -webkit-appearance: none;
      appearance: none;
      width: 100%;
      height: 16px;
      background: #d3d3d3;
      outline: none;

      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 42px;
        height: 42px;
        background: #04AA6D;
        cursor: pointer;
      }

      &::-moz-range-thumb {
        width: 42px;
        height: 42px;
        background: #04AA6D;
        cursor: pointer;
      }
    }
  }

  &-bar {
    position: absolute;
    width: 100%;
    height: 16px;
    border: 1px solid #D3D7E1;
    background-color: #fff;

    &::before {
      position: absolute;
      top: 0;
      left: 0;
      content: '';
      height: 100%;
      border-radius: inherit;
      background: linear-gradient(90deg, #73b4ff 2.5%, #348ef6 94%);
      width: var(--width);
    }
  }

  .rating-section {
    position: relative;
    height: 16px;
  }

  .rating-checkpoint {
    pointer-events: none;
    position: absolute;
    width: 16px;
    height: 16px;
    background-color: #89BEFA;
    border-radius: 50%;
    padding: 1px;
    filter: drop-shadow(-1.19955px 0.399849px 1.59939px rgba(0, 0, 0, 0.22));
    top: 50%;
    transform: translate(-50%, -50%);
    cursor: pointer;

    &-placeholder {
      display: inline-block;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      border: 2px solid #4F9BF3;
      background-color: #fff;
    }
  }

  .rating-marker {
    pointer-events: none;
    position: absolute;
    background-color: #89BEFA;
    border-radius: 50%;
    width: 42px;
    height: 42px;
    padding: 4px;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;

    &-placeholder {
      display: flex;
      background-color: #fff;
      border: 2px solid #4F9BF3;
      border-radius: 50%;
      padding: 3px;
    }

    img {
      max-width: 100%;
      height: auto;
    }

    &-title {
      position: absolute;
      left: 50%;
      font-size: 0.75rem;
      line-height: 1rem;
      font-weight: bold;
      transform: translateX(-50%);
      bottom: -18px;
      width: max-content;
    }
  }

  @media (max-width: 600px) {
    .rating-marker.max {
      .rating-marker-title {
        left: auto;
        right: 0;
        transform: translateX(0);
      }
    }

    .rating-marker.min {
      .rating-marker-title {
        left: 0;
        transform: translateX(0);
      }
    }
  }
}
</style>
